import React, { useEffect, useState } from 'react';
import api from '../api/client';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { Save, Image as ImageIcon } from 'lucide-react';

const Branding = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ header_text: '', subtitle: '', logo_url: '', footer_disclaimer: '', tracking_base_url: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try { const r = await api.get('/settings/branding'); setForm({ header_text: r.data.header_text || '', subtitle: r.data.subtitle || '', logo_url: r.data.logo_url || '', footer_disclaimer: r.data.footer_disclaimer || '', tracking_base_url: r.data.tracking_base_url || '' }); } catch (e) { /* ignore */ }
    })();
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put('/settings/branding', form);
      toast({ title: 'Branding updated' });
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail || e.message, variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <Card><CardContent className="p-6 space-y-5 max-w-2xl">
      <div>
        <h3 className="text-lg font-semibold text-slate-900">Mobilization Form Branding</h3>
        <p className="text-sm text-slate-500">Customize the printable mobilization form header, logo, and footer.</p>
      </div>

      <div>
        <Label htmlFor="header_text">Header Text (Department)</Label>
        <Input id="header_text" data-testid="branding-header" value={form.header_text} onChange={(e) => setField('header_text', e.target.value)} placeholder="PIVI EQUIPMENT SOLUTIONS DEPARTMENT" />
      </div>

      <div>
        <Label htmlFor="subtitle">Form Title (Subtitle)</Label>
        <Input id="subtitle" data-testid="branding-subtitle" value={form.subtitle} onChange={(e) => setField('subtitle', e.target.value)} placeholder="EQUIPMENT MOBILIZATION FORM (ETF + EMF + IETF)" />
      </div>

      <div>
        <Label htmlFor="logo_url">Logo URL</Label>
        <div className="flex gap-2 items-start">
          <Input id="logo_url" data-testid="branding-logo" value={form.logo_url} onChange={(e) => setField('logo_url', e.target.value)} placeholder="https://your-cdn.com/logo.png" />
          {form.logo_url && <img src={form.logo_url} alt="logo preview" className="h-10 w-10 object-contain border rounded" />}
          {!form.logo_url && <div className="h-10 w-10 border rounded grid place-items-center text-slate-400"><ImageIcon className="h-4 w-4" /></div>}
        </div>
        <p className="text-xs text-slate-400 mt-1">Use a public image URL. Recommended size: 256×256 PNG/SVG.</p>
      </div>

      <div>
        <Label htmlFor="footer">Footer Disclaimer</Label>
        <Textarea id="footer" data-testid="branding-footer" rows={2} value={form.footer_disclaimer} onChange={(e) => setField('footer_disclaimer', e.target.value)} placeholder="This is a computer-generated document…" />
      </div>

      <div>
        <Label htmlFor="tracking">QR Tracking Base URL (optional)</Label>
        <Input id="tracking" data-testid="branding-tracking-url" value={form.tracking_base_url} onChange={(e) => setField('tracking_base_url', e.target.value)} placeholder="Defaults to this app's /track/{token}" />
        <p className="text-xs text-slate-400 mt-1">Leave empty to use the current app URL.</p>
      </div>

      <Button onClick={save} disabled={busy} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]" data-testid="branding-save-btn">
        <Save className="h-4 w-4 mr-1.5" /> {busy ? 'Saving…' : 'Save Branding'}
      </Button>
    </CardContent></Card>
  );
};

export default Branding;
